import { ColDef, ICellRendererParams } from "ag-grid-community";
import { EmailLink } from "modules/common/components/links/EmailLink"
import { TelegramLink } from "modules/common/components/links/TelegramLink"
import { PlanrButton } from "modules/common/components/planr/button/Button"
import { GeneralIcon } from "modules/common/components/planr/icon/Generalcon"
import { getFieldLabel } from "modules/common/services/form/fields"
import { formatPhoneNumber } from "modules/common/services/formatting/phone"
import { RowNumberColumn } from "modules/common/services/table/helpers"
import { getColumnSize } from "modules/common/services/table/size-storage"
import { texts } from "modules/common/texts"
import moment from 'moment'
import React, { useCallback } from "react"
import { EmployerSnapshotType, EmployerType, fields } from "../../models/employer"
import Schema from "../details/validation"
import styles from "./EmployerList.module.scss"
import { ProjectPortfolioDictionaryItemType } from 'modules/dictionaries/project-portfolios/models/project-portfolio'

const schema = Schema()

export const columns = (tableName: string, portfolios: ProjectPortfolioDictionaryItemType[]) => {
  const columnSize = getColumnSize(tableName)

  const result: ColDef[] = [
    RowNumberColumn(false, ""),
    {
      headerName: getFieldLabel(fields.name, schema, null),
      field: fields.name,
      width: columnSize(fields.name, 250),
      sortable: true,
      resizable: true,
    },
    {
      headerName: "Задания",
      field: fields.workQuestions,
      width: columnSize(fields.workQuestions, 90),
      sortable: true,
      resizable: true,
      cellRenderer: (params: ICellRendererParams) => {
        const { data } = params
        const getContext = () => params.context as TableContext

        return data && (data.workQuestions > 0 || data.expQuestions > 0) ? (
          <div className={styles.questions}>
            {data.workQuestions > 0 && (
              <span
                onClick={(e: any) => {
                  e.stopPropagation()
                  getContext().goToWorkQuestions(data)
                }}
                className={styles.workData}
              >
                {data.workQuestions}
              </span>
            )}
            {data.expQuestions > 0 && (
              <span
                onClick={(e: any) => {
                  e.stopPropagation()
                  getContext().goToExpQuestions(data)
                }}
                className={styles.expData}
              >
                {data.expQuestions}
              </span>
            )}
          </div>
        ) : null
      },
    },
    {
      headerName: "Отдел",
      field: "department",
      width: columnSize("department", 200),
      valueGetter: ({ data }) => data.department.name,
      sortable: true,
      resizable: true,
    },
    {
      headerName: getFieldLabel(fields.positionId, schema, null),
      field: fields.positionId,
      flex: 1,
      valueGetter: ({ data }) => data.employerPosition.name,
      sortable: true,
      resizable: true,
    },
    {
      headerName: "Орг",
      field: fields.portfolioId,
      width: columnSize(fields.portfolioId, 80),
      sortable: true,
      resizable: true,
      cellRenderer: ({ data }: ICellRendererParams) => {
        const row = portfolios.find((item: any) => item.id === data.portfolioId)
        return row ? <span>{row.label}</span> : null

      }
    },
    {
      headerName: "ЭЦП",
      field: fields.signDateTo,
      sortable: true,
      resizable: true,
      headerClass: "centered",
      cellClass: "centered",
      width: columnSize(fields.signDateTo, 100),
      cellRenderer: ({ data }: ICellRendererParams) => {
        const user = data as EmployerSnapshotType
        if (user.signDateTo) {
          const today = moment().startOf('day')
          // Парсим дату в формате DD.MM.YYYY
          const expiryDate = moment(user.signDateTo, 'DD.MM.YYYY').startOf('day')

          let color = "#00273d" // стандартный цвет

          if (expiryDate.isBefore(today)) {
            color = "#ff0000" // красный - дата истекла
          } else if (expiryDate.diff(today, 'days') <= 21) {
            color = "#ffa600" // желтый - истекает через 3 недели или раньше
          }
          return <span style={{ color: color }}>{user.signDateTo}</span>
        }

        return null
      },
    },
    {
      headerName: "Увол.",
      field: fields.fireDate,
      width: columnSize(fields.fireDate, 120),
      headerClass: "centered",
      cellClass: "centered",
      sortable: true,
      resizable: true,
      comparator: (valueA, valueB, nodeA, nodeB, isInverted) => {
        const dateA = (nodeA.data as EmployerSnapshotType).fireDateSortable
        const dateB = (nodeB.data as EmployerSnapshotType).fireDateSortable
        return dateA.localeCompare(dateB)
      },
    },
    {
      headerName: "Дети",
      field: fields.children,
      width: columnSize(fields.children, 60),
      sortable: true,
      resizable: true,
      cellRenderer: ({ data }: ICellRendererParams) => {
        return data.children && data.children.length > 0 ? <span>{data.children.length}</span> : null

      }
    },
    {
      headerName: "",
      field: fields.canBeReturn,
      width: columnSize(fields.canBeReturn, 60),
      sortable: true,
      resizable: true,
      cellRenderer: ({ value }: ICellRendererParams) => {
        return value ? <GeneralIcon type="general-boomerang" size={20} /> : ""
      },
    },
    {
      headerName: "Совм",
      field: fields.poohBah,
      width: columnSize(fields.poohBah, 80),
      sortable: true,
      resizable: true,
      valueFormatter: ({ value }) => {
        return value ? "Да" : ""
      },
    },

    {
      headerName: getFieldLabel(fields.phone, schema, null),
      field: fields.phone,
      width: columnSize(fields.phone, 150),
      sortable: true,
      resizable: true,
      valueFormatter: ({ value }) => formatPhoneNumber(value),
    },
    {
      headerName: "Телеграм",
      field: fields.tgUser,
      width: columnSize(fields.tgUser, 140),
      sortable: true,
      resizable: true,
      headerClass: "centered",
      cellClass: "centered",
      cellRenderer: ({ value, data }: ICellRendererParams) => {
        const user = data as EmployerSnapshotType
        if (value) {
          return <TelegramLink value={value} />
        }

        if (user.tgRegistered) {
          return <GeneralIcon type="general-check" size={12} />
        }

        return null
      },
    },
    {
      headerName: getFieldLabel(fields.email, schema, null),
      field: fields.email,
      width: columnSize(fields.email, 230),
      sortable: true,
      resizable: true,
      cellRenderer: ({ value }: ICellRendererParams) => {
        return value === "" ? null : <EmailLink value={value} />
      },
    },
  ]

  return result
}

export const columnsRemoved = (tableName: string, canChange: boolean) => {
  const columnSize = getColumnSize(tableName)

  const result: ColDef[] = [
    RowNumberColumn(false, ""),
    {
      headerName: getFieldLabel(fields.name, schema, null),
      field: fields.name,
      width: columnSize(fields.name, 250),
      sortable: true,
      resizable: true,
    },
    {
      headerName: getFieldLabel(fields.login, schema, null),
      field: fields.login,
      width: columnSize(fields.login, 130),
      sortable: true,
      resizable: true,
    },
    {
      headerName: getFieldLabel("department", schema, null),
      field: "department",
      width: columnSize("department", 200),
      valueGetter: ({ data }) => data.department.name,
      sortable: true,
      resizable: true,
    },
    {
      headerName: getFieldLabel(fields.positionId, schema, null),
      field: fields.positionId,
      width: columnSize(fields.positionId, 300),
      valueGetter: ({ data }) => data.employerPosition.name,
      sortable: true,
      resizable: true,
    },
  ]

  if (canChange) {
    result.push({
      headerName: "",
      field: fields.isRemoved,
      width: columnSize(fields.isRemoved, 100),
      cellRenderer: RestoreButton,
      cellClass: "centered",
      sortable: false,
      resizable: false,
    })
  }

  return result
}

function RestoreButton(params: ICellRendererParams) {
  const onClick = useCallback(() => {
    const actions = params.context as RestoreActions
    actions.confirmRestore && actions.confirmRestore(params.data)
  }, [params])

  return <PlanrButton type="neutral" icon="general-undo" onClick={onClick} size="small" title={texts.restore} />
}

export interface RestoreActions {
  confirmRestore?: (user: EmployerSnapshotType) => void
}

export interface TableContext {
  goToWorkQuestions: (emp: EmployerType) => void
  goToExpQuestions: (emp: EmployerType) => void
}
